<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from "vue";
import { useMutation } from "@tanstack/vue-query";
import { NDrawer, NDrawerContent, NTabPane, NTabs, useMessage } from "naive-ui";
import type { Property, BankAccount, Vendor, Comment } from "../models/contador";
import {
    createEntityComment,
    deleteEntityComment,
    updateEntityComment,
} from "../services/contador/comments.service";
import { usePortalBreakpoints } from "../hooks/usePortalBreakpoints";
import RichTextEditor from "./RichTextEditor.vue";
import ConfirmationModal from "./ConfirmationModal.vue";
import NotesDrawerComment from "./NotesDrawerComment.vue";
import { useStorage } from "@vueuse/core";
import type { ComponentPublicInstance } from "vue";
import ProperSelect from "./proper-inputs/proper-select.vue";
import { useOrganization } from "../hooks/useOrganization";
import ProperText from "./proper-inputs/proper-text.vue";
import { getUserEmail } from "../config/firebase.ts";

const { contactEmailToName } = useOrganization();

const props = withDefaults(
    defineProps<{
        show: boolean;
        entity: Property | BankAccount | Vendor | null;
        type: "property" | "bank_account" | "vendor";
        tab?: string;
        autoFocusOnOpen?: boolean;
    }>(),
    {
        type: "property",
        tab: "notes",
        autoFocusOnOpen: false,
    },
);

const emit = defineEmits<{
    (e: "update:show", value: boolean): void;
    (e: "update:tab", value: string): void;
    (e: "comment-created", comment: Comment): void;
    (e: "comment-updated", comment: Comment): void;
    (e: "comment-deleted", commentId: string): void;
    (e: "refetch"): void;
}>();

const { isMobile } = usePortalBreakpoints();
const message = useMessage();

// filters
const selectedAuthor = ref<string | null>(null);
const selectedFilter = ref<"active" | "archived" | "all" | null>("active");
// Search term
const searchTerm = ref<string | null>(null);

// Local state
const noteContent = ref("");
const showDeleteCommentModal = ref(false);
const selectedCommentForDelete = ref<Comment | null>(null);

// Edit state and drafts
const editingCommentId = ref<string | null>(null);
const editingContent = ref<string>("");
const originalEditingContent = ref<string>("");
const editorContainerRef = ref<HTMLElement | null>(null);
const showEditConfirmationModal = ref(false);
const ignoreNextOutsideClick = ref(false);
const commentDrafts = useStorage<Record<string, string>>("entity_comment_drafts", {});

const newNoteEditorRef = ref<ComponentPublicInstance | null>(null);

// new note states
const newNoteDrafts = useStorage<Record<string, string>>("entity_new_note_drafts", {});
const currentEntityKey = computed(() => {
    const id = (props.entity as any)?.id as string | undefined;
    return id ? `${props.type}:${id}` : null;
});

// Computed
const showDrawer = computed({
    get: () => props.show,
    set: (value) => emit("update:show", value),
});

const openedDrawerTab = computed({
    get: () => props.tab,
    set: (value) => emit("update:tab", value),
});

const allComments = computed(() => props.entity?.comments ?? []);

// Helper: only owner can edit a comment
function isCommentOwner(comment: Comment | null | undefined): boolean {
    if (!comment?.author) return false;
    const currentEmail = getUserEmail()?.toLowerCase() ?? null;
    return !!currentEmail && comment.author.toLowerCase() === currentEmail;
}

// Author options for filter
const authorOptions = computed(() => {
    const set = new Map<string, string>();
    for (const c of allComments.value) {
        const email = c.author;
        if (!email) continue;
        const contactName = contactEmailToName(email);
        const label = contactName || email;
        if (!set.has(email)) set.set(email, label);
    }
    const options = Array.from(set.entries()).map(([value, label]) => ({ label, value }));
    return [{ label: "All Authors", value: null as unknown as string | null }, ...options];
});

// Filtered comments based on search, author, and status
const filteredAllComments = computed(() => {
    let list = allComments.value;
    if (selectedAuthor.value) {
        list = list.filter((c) => c.author === selectedAuthor.value);
    }
    const mode = selectedFilter.value ?? "active";
    if (mode === "archived") {
        list = list.filter((c) => Boolean(c.archived));
    } else if (mode === "all") {
    } else {
        list = list.filter((c) => !Boolean(c.archived));
    }
    const q = (searchTerm.value || "").trim().toLowerCase();
    if (q) {
        list = list.filter((c) => (c.text || "").toLowerCase().includes(q));
    }
    return list;
});

// Pinned and regular comments
const pinnedComments = computed(() => filteredAllComments.value.filter((c) => Boolean(c.pinned)));
const regularComments = computed(() => filteredAllComments.value.filter((c) => !Boolean(c.pinned)));

// Set editor container reference
function setEditorContainerRef(el: Element | ComponentPublicInstance | null) {
    if (!el) {
        editorContainerRef.value = null;
        return;
    }
    if (el instanceof HTMLElement) {
        editorContainerRef.value = el;
        return;
    }
    const root = (el as ComponentPublicInstance).$el as Element | null | undefined;
    editorContainerRef.value = root instanceof HTMLElement ? root : null;
}

// Start editing a comment
function startEditingComment(comment: Comment) {
    editingCommentId.value = comment.id;
    const draft = commentDrafts.value[comment.id];
    editingContent.value = draft ?? comment.text ?? "";
    originalEditingContent.value = comment.text ?? "";
}

// Exit editing mode
function exitEditingMode() {
    ignoreNextOutsideClick.value = true;
    editingCommentId.value = null;
    editingContent.value = "";
    originalEditingContent.value = "";
    editorContainerRef.value = null;
}

// Handle global click for detecting outside clicks
function handleGlobalClick(e: MouseEvent) {
    if (!editingCommentId.value || showEditConfirmationModal.value) return;

    if (ignoreNextOutsideClick.value) {
        ignoreNextOutsideClick.value = false;
        return;
    }

    const container = editorContainerRef.value as unknown as Node | null;
    if (!container) return;

    const target = e.target as Node;
    const path = (e as any).composedPath?.() || [];
    const isElement = (n: any): n is Element => n && n.nodeType === 1;

    let clickedInside = false;
    const anyContainer = container as any;

    if (anyContainer && typeof anyContainer.contains === "function") {
        clickedInside = anyContainer.contains(target);
    }

    if (!clickedInside && Array.isArray(path)) {
        clickedInside = path.includes(container);
    }

    if (!clickedInside && Array.isArray(path)) {
        clickedInside = path.some(
            (n: any) => isElement(n) && (n as Element).classList?.contains("rich-text-editor"),
        );
    }

    if (!clickedInside) {
        if (editingContent.value !== originalEditingContent.value) {
            showEditConfirmationModal.value = true;
            e.preventDefault();
            e.stopPropagation();
            (e as any).stopImmediatePropagation?.();
        }
    }
}

onMounted(() => {
    document.addEventListener("click", handleGlobalClick, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleGlobalClick, true);
});

// Mutations
const { mutate: createCommentMutation, isPending: isSendingComment } = useMutation({
    mutationFn: async () => {
        if (!props.entity?.id) {
            throw new Error();
        }
        return await createEntityComment({
            commentableType: props.type,
            commentableId: props.entity.id,
            text: noteContent.value,
        });
    },
    onSuccess(newComment: Comment) {
        emit("comment-created", newComment);
        noteContent.value = "";
        const key = currentEntityKey.value;
        if (key && newNoteDrafts.value[key]) {
            const { [key]: _omit, ...rest } = newNoteDrafts.value;
            newNoteDrafts.value = rest;
        }
        message.success("Note added");
        emit("refetch");
    },
    onError(error: any) {
        message.error(error?.message || "Failed to add note");
    },
});

const { mutate: updateCommentMutation, isPending: isUpdatingComment } = useMutation({
    mutationFn: async (payload: { id: string; text: string }) => {
        const { id, text } = payload;
        return await updateEntityComment(id, { text });
    },
    onSuccess(updated: Comment) {
        emit("comment-updated", updated);
        if (commentDrafts.value[updated.id]) {
            const { [updated.id]: _, ...rest } = commentDrafts.value;
            commentDrafts.value = rest;
        }
        exitEditingMode();
        message.success("Note updated");
        emit("refetch");
    },
    onError(err: any) {
        message.error(err?.message || "Failed to update note");
    },
});

function saveChanges() {
    const id = editingCommentId.value;
    if (!id) return;
    const comment = allComments.value.find((c) => c.id === id);
    if (!comment || !isCommentOwner(comment)) {
        message.warning("Only the note owner can edit this note");
        exitEditingMode();
        return;
    }
    ignoreNextOutsideClick.value = true;
    updateCommentMutation({ id, text: editingContent.value });
}

const { mutate: togglePinCommentMutation } = useMutation({
    mutationFn: async (payload: { id: string; pinned: boolean }) => {
        const { id, pinned } = payload;
        return await updateEntityComment(id, { pinned });
    },
    onSuccess(updated: Comment) {
        emit("comment-updated", updated);
        message.success(updated.pinned ? "Note pinned" : "Note unpinned");
        emit("refetch");
    },
    onError(err: any) {
        message.error(err?.message || "Failed to toggle pin");
    },
});

const { mutate: toggleArchiveCommentMutation } = useMutation({
    mutationFn: async (payload: { id: string; archived: string | null; pinned?: boolean }) => {
        const { id, archived, pinned } = payload;
        return await updateEntityComment(id, { archived, pinned });
    },
    onSuccess(updated: Comment) {
        emit("comment-updated", updated);
        message.success(updated.archived ? "Note archived" : "Note unarchived");
        emit("refetch");
    },
    onError(err: any) {
        message.error(err?.message || "Failed to toggle archive");
    },
});

// Comment action handlers
function handleCommentEdit(comment: Comment) {
    if (!isCommentOwner(comment)) {
        message.warning("Only the note owner can edit this note");
        return;
    }
    startEditingComment(comment);
}

function handleCommentDelete(comment: Comment) {
    if (!isCommentOwner(comment)) {
        message.warning("Only the note owner can delete this note");
        return;
    }
    selectedCommentForDelete.value = comment;
    showDeleteCommentModal.value = true;
}

function handleCommentPin(comment: Comment) {
    if (comment.archived) {
        message.warning("Cannot pin an archived Note");
        return;
    }
    togglePinCommentMutation({ id: comment.id, pinned: !Boolean(comment.pinned) });
}

function handleCommentArchive(comment: Comment) {
    const nextArchived = comment.archived ? null : new Date().toISOString();
    const nextPinned = nextArchived ? false : comment.pinned;
    toggleArchiveCommentMutation({
        id: comment.id,
        archived: nextArchived,
        pinned: nextPinned ?? undefined,
    });
}

function handleDeleteDraft(commentId: string) {
    deleteDraft(commentId);
    exitEditingMode();
}

async function confirmDeleteSelectedComment() {
    if (!selectedCommentForDelete.value?.id) {
        showDeleteCommentModal.value = false;
        selectedCommentForDelete.value = null;
        return;
    }
    if (!isCommentOwner(selectedCommentForDelete.value)) {
        message.warning("Only the note owner can delete this note");
        showDeleteCommentModal.value = false;
        selectedCommentForDelete.value = null;
        return;
    }
    try {
        await deleteEntityComment(selectedCommentForDelete.value.id);
        emit("comment-deleted", selectedCommentForDelete.value.id);
        message.warning("Note deleted");
        emit("refetch");
    } catch (e: any) {
        message.error(e?.message || "Failed to delete Note");
    } finally {
        showDeleteCommentModal.value = false;
        selectedCommentForDelete.value = null;
    }
}

function saveDraftAndExit() {
    const id = editingCommentId.value;
    if (!id) return;
    commentDrafts.value = { ...commentDrafts.value, [id]: editingContent.value };
    showEditConfirmationModal.value = false;
    exitEditingMode();
    message.info("Draft saved");
}

function continueEditing() {
    showEditConfirmationModal.value = false;
}

function deleteDraft(id: string) {
    const { [id]: _, ...rest } = commentDrafts.value;
    commentDrafts.value = rest;
}

function setFilter(mode: "active" | "archived" | "all") {
    selectedFilter.value = mode;
    selectedAuthor.value = null;
}
function setAuthor(email: string | null) {
    selectedAuthor.value = email;
    if (email === null) {
        selectedFilter.value = selectedFilter.value ?? "active";
    }
}
function clearAuthor() {
    selectedAuthor.value = null;
    selectedFilter.value = "active";
}

watch(
    () => currentEntityKey.value,
    (key) => {
        noteContent.value = key ? (newNoteDrafts.value[key] ?? "") : "";
    },
    { immediate: true },
);

watch(
    () => noteContent.value,
    (val) => {
        const key = currentEntityKey.value;
        if (!key) return;
        const trimmed = (val || "").trim();
        if (trimmed) {
            newNoteDrafts.value = { ...newNoteDrafts.value, [key]: val };
        } else {
            const { [key]: _omit, ...rest } = newNoteDrafts.value;
            newNoteDrafts.value = rest;
        }
    },
);

watch(
    () => props.show,
    async (isOpen) => {
        if (!isOpen) return;
        // Ensure notes tab is selected before focusing
        if (openedDrawerTab.value !== "notes") return;
        if (!props.autoFocusOnOpen) return;
        await nextTick();
        const editorComp = newNoteEditorRef.value as any;
        if (editorComp && typeof editorComp.focusInput === "function") {
            editorComp.focusInput();
        }
    },
);
</script>

<template>
    <n-drawer
        v-model:show="showDrawer"
        :width="isMobile ? '100%' : 500"
        placement="right"
        :mask-closable="!editingCommentId"
    >
        <n-drawer-content
            :title="`${
                type === 'property' || type === 'vendor'
                    ? (entity as Property | Vendor)?.name
                    : type === 'bank_account'
                      ? `${(entity as BankAccount)?.account_number} (${(entity as BankAccount)?.account_name})`
                      : '-'
            } - Notes`"
            :closable="true"
        >
            <n-tabs type="line" animated v-model:value="openedDrawerTab">
                <n-tab-pane name="notes" tab="Notes">
                    <!-- Search input -->
                    <div class="mb-3">
                        <proper-text
                            :model-value="searchTerm"
                            placeholder="Search notes..."
                            clearable
                            :live-preview="false"
                            @update:modelValue="(v) => (searchTerm = v ?? '')"
                            @clear="searchTerm = null"
                        />
                    </div>

                    <div class="mb-4 flex gap-2 flex-wrap items-center">
                        <button
                            :class="[
                                selectedFilter === 'active' ? 'button' : 'secondary-button',
                                '!font-regular',
                            ]"
                            @click="setFilter('active')"
                        >
                            Active
                        </button>
                        <button
                            :class="[
                                selectedFilter === 'archived' ? 'button' : 'secondary-button',
                                '!font-regular',
                            ]"
                            @click="setFilter('archived')"
                        >
                            Archived
                        </button>
                        <button
                            :class="[
                                selectedFilter === 'all' ? 'button' : 'secondary-button',
                                '!font-regular',
                            ]"
                            @click="setFilter('all')"
                        >
                            All
                        </button>
                        <proper-select
                            :model-value="selectedAuthor"
                            :options="authorOptions as any"
                            placeholder="All Authors"
                            class="w-full sm:w-40"
                            clearable
                            @clear="clearAuthor"
                            @update:model-value="(v) => setAuthor(v as string | null)"
                        />
                    </div>

                    <div class="mb-4">
                        <rich-text-editor
                            ref="newNoteEditorRef"
                            v-model="noteContent"
                            :show-file-upload="true"
                            :show-submit-button="true"
                            button-text="Add Note"
                            :is-submitting="isSendingComment"
                            :submit-disabled="!entity"
                            placeholder="Write a note..."
                            @submit="createCommentMutation()"
                        />
                    </div>

                    <!-- Pinned comments section -->
                    <div v-if="pinnedComments.length" class="mb-2">
                        <p class="flex items-center gap-1 mb-2">
                            <span
                                class="material-symbols-outlined text-amber-600 text-lg"
                                title="Pinned"
                                aria-label="Pinned"
                            >
                                push_pin
                            </span>
                            <span class="text-xs uppercase text-gray-500">Pinned</span>
                        </p>

                        <ul>
                            <notes-drawer-comment
                                v-for="comment in pinnedComments"
                                :key="comment.id"
                                :comment="comment"
                                :is-editing="editingCommentId === comment.id"
                                :editing-content="editingContent"
                                :original-content="originalEditingContent"
                                :has-draft="!!commentDrafts[comment.id]"
                                :is-updating="isUpdatingComment"
                                @update:editing-content="editingContent = $event"
                                @edit="handleCommentEdit(comment)"
                                @delete="handleCommentDelete(comment)"
                                @pin="handleCommentPin(comment)"
                                @archive="handleCommentArchive(comment)"
                                @save="saveChanges"
                                @cancel="exitEditingMode"
                                @delete-draft="handleDeleteDraft(comment.id)"
                                @set-editor-ref="setEditorContainerRef"
                            />
                        </ul>
                        <div class="h-px bg-gray-200 mt-2 mb-4"></div>
                    </div>

                    <div>
                        <ul>
                            <notes-drawer-comment
                                v-for="comment in regularComments"
                                :key="comment.id"
                                :comment="comment"
                                :is-editing="editingCommentId === comment.id"
                                :editing-content="editingContent"
                                :original-content="originalEditingContent"
                                :has-draft="!!commentDrafts[comment.id]"
                                :is-updating="isUpdatingComment"
                                @update:editing-content="editingContent = $event"
                                @edit="handleCommentEdit(comment)"
                                @delete="handleCommentDelete(comment)"
                                @pin="handleCommentPin(comment)"
                                @archive="handleCommentArchive(comment)"
                                @save="saveChanges"
                                @cancel="exitEditingMode"
                                @delete-draft="handleDeleteDraft(comment.id)"
                                @set-editor-ref="setEditorContainerRef"
                            />
                        </ul>
                    </div>
                </n-tab-pane>
                <n-tab-pane name="ativity" tab="Activity"> </n-tab-pane>
            </n-tabs>
        </n-drawer-content>
    </n-drawer>

    <confirmation-modal
        v-if="showDeleteCommentModal"
        danger
        title="Confirm Delete Comment"
        content="Are you sure you want to delete this comment?"
        negative-text="Cancel"
        positive-text="Delete"
        @positive-click="confirmDeleteSelectedComment"
        @negative-click="showDeleteCommentModal = false"
    />

    <confirmation-modal
        v-if="showEditConfirmationModal"
        title="Unsaved changes"
        content="You have unsaved changes. What would you like to do?"
        negative-text="Go back"
        positive-text="Save draft locally"
        @positive-click="saveDraftAndExit"
        @negative-click="continueEditing"
    />
</template>
